import { useState, useEffect, useRef, useCallback } from 'react'

interface WatchlistItem {
  protocol: string
  tvl_usd: number
  change_24h: number
  change_7d: number
  health: 'green' | 'amber' | 'red'
  last_updated: number
}

interface Alert {
  id: number
  protocol: string
  severity: string
  message: string
  timestamp: number
  tvl_change_pct?: number
}

interface Signal {
  signal_id: string
  timestamp: number
  direction: string
  token: string
  confidence: number
  confidence_tier: string
  reasoning: string[]
  entry_price: number
  suggested_size_pct: number
}

interface Trade {
  trade_id: string
  signal_id: string
  token: string
  direction: string
  entry_price: number
  exit_price: number | null
  entry_time: number
  exit_time: number | null
  size_usd: number
  pnl_usd: number | null
  pnl_pct: number | null
  status: string
  stop_loss: number
  take_profit: number
}

interface PipelineStatus {
  status: string
  last_scan: string | null
  last_signal: string | null
  last_trade: string | null
  open_positions: number
  total_signals: number
  scan_interval_seconds: number
}

interface PortfolioMetrics {
  starting_capital: number
  current_value: number
  capital_at_risk: {
    open_exposure_usd: number
    cash_reserve_usd: number
    exposure_pct: number
  }
  drawdown: {
    peak_value: number
    current_drawdown_pct: number
    max_drawdown_pct: number
  }
  win_loss: {
    total_trades: number
    wins: number
    losses: number
    win_rate_pct: number | null
    avg_win_usd: number | null
    avg_loss_usd: number | null
    reward_risk_ratio: number | null
  }
  pnl: {
    realized_usd: number
    unrealized_usd: number
    total_usd: number
    total_pct: number
  }
}

interface Stats {
  signals: number
  trades: number
  alerts: number
  price_points: number
  tokens_tracked: number
}

function useFetch<T>(path: string, interval = 30000): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`/api/kanary${path.replace('/api/', '/')}`)
      if (!res.ok) throw new Error(`${res.status}`)
      const json = await res.json()
      setData(json)
      setError(null)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [path])

  useEffect(() => {
    fetchData()
    const id = setInterval(fetchData, interval)
    return () => clearInterval(id)
  }, [fetchData, interval])

  return { data, loading, error }
}

function formatUsd(n: number, decimals = 2): string {
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`
  return `$${n.toFixed(decimals)}`
}

function formatPct(n: number): string {
  const sign = n >= 0 ? '+' : ''
  return `${sign}${(n * 100).toFixed(1)}%`
}

function formatTime(ts: number): string {
  return new Date(ts * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function timeAgo(ts: number): string {
  const diff = Math.floor(Date.now() / 1000) - ts
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

// Colors
const KANARY_GREEN = '#00E676'
const KANARY_RED = '#FF5252'
const KANARY_AMBER = '#FFD740'
const KANARY_ACCENT = '#00BCD4'
const KANARY_DIM = 'rgba(255,255,255,0.35)'

// ─── Pipeline Status Bar ───

function PipelineBar() {
  const { data } = useFetch<PipelineStatus>('/api/pipeline-status', 10000)
  if (!data) return null

  const scanning = data.status === 'running'
  const lastScan = data.last_scan ? timeAgo(new Date(data.last_scan).getTime() / 1000) : 'never'

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16, padding: '10px 16px',
      background: scanning ? 'rgba(0,230,118,0.08)' : 'rgba(255,82,82,0.08)',
      border: `1px solid ${scanning ? 'rgba(0,230,118,0.2)' : 'rgba(255,82,82,0.2)'}`,
      borderRadius: 8, marginBottom: 16,
    }}>
      <div style={{
        width: 8, height: 8, borderRadius: '50%',
        background: scanning ? KANARY_GREEN : KANARY_RED,
        boxShadow: `0 0 6px ${scanning ? KANARY_GREEN : KANARY_RED}`,
        animation: scanning ? 'pulse 2s infinite' : 'none',
      }} />
      <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>
        {scanning ? 'Pipeline Active' : 'Pipeline Offline'}
      </span>
      <span style={{ fontSize: 12, color: KANARY_DIM }}>
        Last scan: {lastScan}
      </span>
      <span style={{ fontSize: 12, color: KANARY_DIM, marginLeft: 'auto' }}>
        {data.total_signals} signals | {data.open_positions} open positions | every {data.scan_interval_seconds}s
      </span>
    </div>
  )
}

// ─── Stats Row ───

function StatsRow() {
  const { data: stats } = useFetch<Stats>('/api/stats', 60000)
  const { data: metrics } = useFetch<PortfolioMetrics>('/api/portfolio-metrics', 15000)

  if (!stats || !metrics) return null

  const cards = [
    { label: 'Portfolio', value: formatUsd(metrics.current_value), sub: formatPct(metrics.pnl.total_pct / 100), color: metrics.pnl.total_usd >= 0 ? KANARY_GREEN : KANARY_RED },
    { label: 'P&L', value: `${metrics.pnl.total_usd >= 0 ? '+' : ''}${formatUsd(metrics.pnl.total_usd)}`, sub: `${metrics.win_loss.total_trades} trades`, color: metrics.pnl.total_usd >= 0 ? KANARY_GREEN : KANARY_RED },
    { label: 'Win Rate', value: metrics.win_loss.win_rate_pct !== null ? `${metrics.win_loss.win_rate_pct.toFixed(0)}%` : '—', sub: `${metrics.win_loss.wins}W / ${metrics.win_loss.losses}L`, color: KANARY_ACCENT },
    { label: 'Protocols', value: `${stats.tokens_tracked}`, sub: `${stats.alerts.toLocaleString()} alerts`, color: KANARY_ACCENT },
    { label: 'Max Drawdown', value: `${metrics.drawdown.max_drawdown_pct.toFixed(1)}%`, sub: `current: ${metrics.drawdown.current_drawdown_pct.toFixed(1)}%`, color: metrics.drawdown.current_drawdown_pct > 10 ? KANARY_RED : KANARY_DIM },
  ]

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
      {cards.map(c => (
        <div key={c.label} style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px',
        }}>
          <div style={{ fontSize: 11, color: KANARY_DIM, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{c.label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: c.color, fontFamily: 'monospace' }}>{c.value}</div>
          <div style={{ fontSize: 11, color: KANARY_DIM, marginTop: 2 }}>{c.sub}</div>
        </div>
      ))}
    </div>
  )
}

// ─── Protocol Watchlist ───

function Watchlist() {
  const { data } = useFetch<{ watchlist: WatchlistItem[] }>('/api/watchlist', 30000)

  if (!data) return <PanelSkeleton title="Protocol Health" />

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span>Protocol Health</span>
        <span style={{ fontSize: 11, color: KANARY_DIM }}>{data.watchlist.length} tracked</span>
      </div>
      <div style={{ maxHeight: 360, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={thStyle}>Protocol</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>TVL</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>24h</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>7d</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.watchlist.map(p => (
              <tr key={p.protocol} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '8px 8px', color: 'var(--text-primary)', fontWeight: 500 }}>
                  {p.protocol}
                </td>
                <td style={{ padding: '8px 8px', textAlign: 'right', color: KANARY_DIM, fontFamily: 'monospace' }}>
                  {formatUsd(p.tvl_usd)}
                </td>
                <td style={{
                  padding: '8px 8px', textAlign: 'right', fontFamily: 'monospace',
                  color: p.change_24h >= 0 ? KANARY_GREEN : KANARY_RED,
                }}>
                  {formatPct(p.change_24h)}
                </td>
                <td style={{
                  padding: '8px 8px', textAlign: 'right', fontFamily: 'monospace',
                  color: p.change_7d >= 0 ? KANARY_GREEN : KANARY_RED,
                }}>
                  {formatPct(p.change_7d)}
                </td>
                <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                  <HealthDot health={p.health} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function HealthDot({ health }: { health: 'green' | 'amber' | 'red' }) {
  const color = health === 'green' ? KANARY_GREEN : health === 'amber' ? KANARY_AMBER : KANARY_RED
  const label = health === 'green' ? 'Normal' : health === 'amber' ? 'Elevated' : 'Cascade'
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%', background: color,
        boxShadow: health !== 'green' ? `0 0 4px ${color}` : 'none',
      }} />
      <span style={{ fontSize: 11, color }}>{label}</span>
    </span>
  )
}

// ─── Alert Feed ───

function AlertFeed() {
  const [severityFilter, setSeverityFilter] = useState<string>('')
  const { data } = useFetch<{ alerts: Alert[] }>(`/api/alerts?limit=100${severityFilter ? `&severity=${severityFilter}` : ''}`, 15000)

  if (!data) return <PanelSkeleton title="Alert Feed" />

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span>Alert Feed</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {['', 'CRITICAL', 'WARNING', 'INFO'].map(s => (
            <button
              key={s}
              onClick={() => setSeverityFilter(s)}
              style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
                background: severityFilter === s ? 'rgba(0,188,212,0.15)' : 'transparent',
                color: severityFilter === s ? KANARY_ACCENT : KANARY_DIM,
                border: `1px solid ${severityFilter === s ? KANARY_ACCENT : 'var(--border)'}`,
              }}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>
      <div style={{ maxHeight: 360, overflowY: 'auto' }}>
        {data.alerts.length === 0 ? (
          <EmptyState text="No alerts matching filter" />
        ) : (
          data.alerts.map(a => (
            <div key={a.id} style={{
              padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)',
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <SeverityBadge severity={a.severity} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.4 }}>
                  <span style={{ fontWeight: 600 }}>{a.protocol}</span>
                  {' — '}{a.message?.slice(0, 120)}
                </div>
                <div style={{ fontSize: 11, color: KANARY_DIM, marginTop: 2 }}>
                  {formatTime(a.timestamp)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

function SeverityBadge({ severity }: { severity: string }) {
  const color = severity === 'CRITICAL' ? KANARY_RED : severity === 'WARNING' ? KANARY_AMBER : 'rgba(255,255,255,0.3)'
  return (
    <span style={{
      fontSize: 9, padding: '2px 6px', borderRadius: 3, fontWeight: 700,
      background: `${color}22`, color, letterSpacing: 0.5, whiteSpace: 'nowrap',
      border: `1px solid ${color}44`,
    }}>
      {severity}
    </span>
  )
}

// ─── Signal Feed ───

function SignalFeed() {
  const { data } = useFetch<{ signals: Signal[] }>('/api/signals?limit=20', 15000)

  if (!data) return <PanelSkeleton title="Signals" />

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span>Signals</span>
        <span style={{ fontSize: 11, color: KANARY_DIM }}>{data.signals.length} total</span>
      </div>
      <div style={{ maxHeight: 360, overflowY: 'auto' }}>
        {data.signals.length === 0 ? (
          <EmptyState text="No cascade signals detected yet — watching 18 protocols" />
        ) : (
          data.signals.map(s => (
            <div key={s.signal_id} style={{
              padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 3,
                  background: s.direction === 'SHORT' ? 'rgba(255,82,82,0.15)' : 'rgba(0,230,118,0.15)',
                  color: s.direction === 'SHORT' ? KANARY_RED : KANARY_GREEN,
                  border: `1px solid ${s.direction === 'SHORT' ? 'rgba(255,82,82,0.3)' : 'rgba(0,230,118,0.3)'}`,
                }}>
                  {s.direction} {s.token}
                </span>
                <span style={{
                  fontSize: 11, fontFamily: 'monospace',
                  color: s.confidence >= 0.9 ? KANARY_GREEN : s.confidence >= 0.7 ? KANARY_AMBER : KANARY_DIM,
                }}>
                  {(s.confidence * 100).toFixed(0)}% confidence
                </span>
                <span style={{ fontSize: 11, color: KANARY_DIM, marginLeft: 'auto' }}>
                  {formatTime(s.timestamp)}
                </span>
              </div>
              {s.reasoning && s.reasoning.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 4 }}>
                  {s.reasoning.slice(0, 3).map((r, i) => (
                    <div key={i}>• {r}</div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ─── Live Positions ───

function Positions() {
  const { data } = useFetch<{ positions: Trade[] }>('/api/positions', 10000)

  if (!data) return <PanelSkeleton title="Live Positions" />

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span>Live Positions</span>
        <span style={{ fontSize: 11, color: KANARY_DIM }}>{data.positions.length} open</span>
      </div>
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {data.positions.length === 0 ? (
          <EmptyState text="No open positions — desk is idle" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={thStyle}>Token</th>
                <th style={thStyle}>Direction</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Entry</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Size</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>SL / TP</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Held</th>
              </tr>
            </thead>
            <tbody>
              {data.positions.map(p => (
                <tr key={p.trade_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '8px', color: 'var(--text-primary)', fontWeight: 600 }}>{p.token}</td>
                  <td style={{ padding: '8px' }}>
                    <span style={{ color: p.direction === 'SHORT' ? KANARY_RED : KANARY_GREEN, fontWeight: 600 }}>
                      {p.direction}
                    </span>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', color: KANARY_DIM }}>
                    ${p.entry_price.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', color: KANARY_DIM }}>
                    {formatUsd(p.size_usd)}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', fontSize: 11 }}>
                    <span style={{ color: KANARY_RED }}>${p.stop_loss?.toFixed(2) || '—'}</span>
                    {' / '}
                    <span style={{ color: KANARY_GREEN }}>${p.take_profit?.toFixed(2) || '—'}</span>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', color: KANARY_DIM, fontSize: 11 }}>
                    {timeAgo(p.entry_time)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ─── Trade History ───

function TradeHistory() {
  const { data } = useFetch<{ trades: Trade[] }>('/api/trades?limit=30', 30000)

  if (!data) return <PanelSkeleton title="Trade History" />

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span>Trade History</span>
        <span style={{ fontSize: 11, color: KANARY_DIM }}>{data.trades.length} trades</span>
      </div>
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {data.trades.length === 0 ? (
          <EmptyState text="No completed trades yet" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={thStyle}>Token</th>
                <th style={thStyle}>Dir</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Entry</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Exit</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>P&L</th>
                <th style={thStyle}>Result</th>
              </tr>
            </thead>
            <tbody>
              {data.trades.map(t => (
                <tr key={t.trade_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '8px', fontWeight: 600, color: 'var(--text-primary)' }}>{t.token}</td>
                  <td style={{ padding: '8px', color: t.direction === 'SHORT' ? KANARY_RED : KANARY_GREEN, fontWeight: 600 }}>
                    {t.direction}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', color: KANARY_DIM }}>
                    ${t.entry_price.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace', color: KANARY_DIM }}>
                    {t.exit_price ? `$${t.exit_price.toFixed(2)}` : '—'}
                  </td>
                  <td style={{
                    padding: '8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600,
                    color: (t.pnl_usd || 0) >= 0 ? KANARY_GREEN : KANARY_RED,
                  }}>
                    {t.pnl_usd !== null ? `${t.pnl_usd >= 0 ? '+' : ''}$${t.pnl_usd.toFixed(2)}` : '—'}
                  </td>
                  <td style={{ padding: '8px' }}>
                    <StatusPill status={t.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function StatusPill({ status }: { status: string }) {
  const color = status === 'closed' ? KANARY_GREEN : status === 'stopped' ? KANARY_RED : KANARY_AMBER
  return (
    <span style={{
      fontSize: 10, padding: '2px 6px', borderRadius: 3,
      background: `${color}15`, color, fontWeight: 600,
      border: `1px solid ${color}33`,
    }}>
      {status}
    </span>
  )
}

// ─── Live Event Ticker (SSE) ───

function EventTicker() {
  const [events, setEvents] = useState<any[]>([])
  const sourceRef = useRef<EventSource | null>(null)

  useEffect(() => {
    const es = new EventSource(`/api/kanary/events`)
    sourceRef.current = es

    es.onmessage = (e) => {
      try {
        const evt = JSON.parse(e.data)
        setEvents(prev => [evt, ...prev].slice(0, 50))
      } catch {}
    }

    return () => es.close()
  }, [])

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: KANARY_GREEN,
            animation: 'pulse 2s infinite',
          }} />
          Live Feed
        </span>
        <span style={{ fontSize: 11, color: KANARY_DIM }}>{events.length} events</span>
      </div>
      <div style={{ maxHeight: 200, overflowY: 'auto', fontFamily: 'monospace', fontSize: 11 }}>
        {events.length === 0 ? (
          <EmptyState text="Listening for pipeline events..." />
        ) : (
          events.map((evt, i) => (
            <div key={i} style={{
              padding: '4px 12px', borderBottom: '1px solid rgba(255,255,255,0.03)',
              color: evt.stage === 'signal' ? KANARY_ACCENT : evt.stage === 'executing' ? KANARY_GREEN : KANARY_DIM,
            }}>
              <span style={{ color: 'rgba(255,255,255,0.2)', marginRight: 8 }}>
                {new Date(evt.timestamp).toLocaleTimeString()}
              </span>
              [{evt.stage}] {evt.summary || JSON.stringify(evt.data).slice(0, 80)}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ─── Shared Components ───

function EmptyState({ text }: { text: string }) {
  return (
    <div style={{
      padding: '32px 16px', textAlign: 'center', color: KANARY_DIM, fontSize: 13,
    }}>
      {text}
    </div>
  )
}

function PanelSkeleton({ title }: { title: string }) {
  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}><span>{title}</span></div>
      <div style={{ padding: 24, textAlign: 'center', color: KANARY_DIM, fontSize: 12 }}>Loading...</div>
    </div>
  )
}

// ─── Shared Styles ───

const panelStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  overflow: 'hidden',
}

const panelHeaderStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '10px 14px', borderBottom: '1px solid var(--border)',
  fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
}

const thStyle: React.CSSProperties = {
  padding: '6px 8px', textAlign: 'left', fontSize: 11, fontWeight: 600,
  color: KANARY_DIM, textTransform: 'uppercase', letterSpacing: 0.5,
}

// ─── Main Page ───

export default function KanaryPage() {
  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          Kanary
        </h1>
        <span style={{ fontSize: 13, color: KANARY_DIM }}>TVL Cascade Intelligence</span>
      </div>

      {/* Pipeline status */}
      <PipelineBar />

      {/* Stats cards */}
      <StatsRow />

      {/* Main grid: 2 columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Watchlist />
        <AlertFeed />
      </div>

      {/* Middle row: signals + positions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <SignalFeed />
        <Positions />
      </div>

      {/* Bottom row: trade history + live feed */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <TradeHistory />
        <EventTicker />
      </div>
    </div>
  )
}
